import type * as React from 'react';

import type { DateTime, Interval } from 'luxon';
import type { TaggedUnion } from 'type-fest';

import type { Business } from 'app-components/flux/business/businessTypes';
import type { FilterObject } from 'app-components/flux/filters/filtersTypes';
import type {
  AccountingType,
  ChartType,
  ColumnType,
  DateRangeType,
  DisplayOptionsType,
  OptionsType,
  ReportNameType,
  ReportSubType,
  ReportType,
  RowType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import type { GeneratedReportRanges, Timestep } from 'app-components/utils/date';

// Define types for each property in the report configuration
export type ReportSubTypeWebReports = ReportSubType | 'REFUND_TRACKER' | 'SAVINGS' | 'NONE';

// Define the structure of a single tab configuration

export type TabConfig = {
  name: string;
  accountingType: AccountingType;
  reportType: ReportType;
  reportSubType: ReportSubTypeWebReports;
  dateRange: DateRangeType;
  groupBy: RowType;
  segmentBy: ColumnType;
  timestep: Timestep;
  chartType: ChartType;
  options?: OptionsType;
  displayOptions?: DisplayOptionsType;
};

export type ReportsState = {
  id?: string; // Unique identifier for the report
  clientId?: string; // Optional client ID for the report

  name: string;
  usage?: string[];

  accountingType: AccountingType;
  reportType: ReportType;
  reportSubType: ReportSubTypeWebReports;
  options?: OptionsType;
  displayOptions: DisplayOptionsType;

  dateRange: DateRangeType;
  startDate?: DateTime;
  endDate?: DateTime;
  today: DateTime;

  groupBy: RowType;
  segmentBy: ColumnType;
  timestep?: Timestep;

  filterId?: string;
  filter: FilterObject;

  chartType?: ChartType;
  breadcrumbs: any[];
  fallbackSelect?: React.RefObject<null>;
  defaultBusiness?: Business;
  tabName: ReportNameType;
  loading: boolean;
};

export const UPDATE_BREADCRUMBS = 'UPDATE_BREADCRUMBS';
export const UPDATE_CHART_TYPE = 'UPDATE_CHART_TYPE';
export const UPDATE_DATE_RANGE = 'UPDATE_DATE_RANGE';
export const UPDATE_LOADING = 'UPDATE_LOADING';
export const UPDATE_FILTER = 'UPDATE_FILTER';
export const UPDATE_OPTIONS = 'UPDATE_OPTIONS';
export const UPDATE_USAGE = 'UPDATE_USAGE';
export const UPDATE_REPORT_SUBTYPE = 'UPDATE_REPORT_SUBTYPE';
export const UPDATE_REPORT_TYPE = 'UPDATE_REPORT_TYPE';
export const UPDATE_DISPLAY_OPTIONS = 'UPDATE_DISPLAY_OPTIONS';
export const UPDATE_SEGMENT_BY = 'UPDATE_SEGMENT_BY';
export const UPDATE_TIMESTEP = 'UPDATE_TIMESTEP';
export const UPDATE_GROUP_BY = 'UPDATE_GROUP_BY';
export const UPDATE_OVERTIME = 'UPDATE_OVERTIME';
export const UPDATE_ACCOUNTING_TYPE = 'UPDATE_ACCOUNTING_TYPE';
export const UPDATE_TAB_NAME = 'UPDATE_TAB_NAME';
export const RESET_DISPLAY_OPTIONS = 'RESET_DISPLAY_OPTIONS';
export const RESET_REPORT = 'RESET_REPORT';

export type ReportAction = TaggedUnion<
  'type',
  {
    UPDATE_BREADCRUMBS: { payload: { breadcrumbs: any } };
    UPDATE_CHART_TYPE: { payload: { chartType: ChartType } };
    UPDATE_DATE_RANGE: { payload: { dateRange: { id: GeneratedReportRanges; interval: Interval } } };
    UPDATE_LOADING: { payload: { loading: boolean } };
    UPDATE_FILTER: { payload: any };
    UPDATE_OPTIONS: { payload: { options: OptionsType } };
    UPDATE_USAGE: { payload: { usage: string[] } };
    UPDATE_REPORT_SUBTYPE: { payload: { reportSubtype: ReportSubType } };
    UPDATE_REPORT_TYPE: { payload: { reportType: ReportType } };
    UPDATE_TIMESTEP: { payload: any };
    UPDATE_DISPLAY_OPTIONS: { payload: { displayOptions: DisplayOptionsType } };
    UPDATE_SEGMENT_BY: { payload: { segmentBy: ColumnType } };
    UPDATE_ACCOUNTING_TYPE: { payload: { accountingType: AccountingType } };
    RESET_DISPLAY_OPTIONS: { payload: { displayOptions: DisplayOptionsType; accountingType: AccountingType } };
    RESET_REPORT: { payload: { state: ReportsState } };
    UPDATE_GROUP_BY: { payload: { groupBy: RowType } };
    UPDATE_OVERTIME: { payload: { overTime: Timestep } };
    UPDATE_TAB_NAME: { payload: { tabName: ReportNameType } };
  }
>;
